import { useCallback } from 'react';

import { useLocation, useParams } from 'react-router-dom';

import { FASTSHIFT_PATHS, FASTSHIFT_PATH_KEYS, FASTSHIFT_EAP_HOME_PATH } from '../routes/constants';

const PATHS_KEYS = {
	HOME: 'home',
	SELECTION: 'selection',
	PARTNER_RESOURCES: 'partnerResources',
	DOMAIN_SCOPED_OVERVIEW: 'domainScopedOverview',
	DOMAIN_SCOPED_PLANNING: 'domainScopedPlanning',
	DOMAIN_SCOPED_TICKETS: 'domainScopedTickets',
	DOMAIN_SCOPED_RESOURCES: 'domainScopedResources',
	EAP_HOME: 'eapHome',
} as const;

type UseRoutes = () => {
	domainId: string | undefined;
	fullPathname: string;
	paths: Record<(typeof PATHS_KEYS)[keyof typeof PATHS_KEYS], string>;
	getPathByDomainId: (path: string, targetDomainId: string) => string;
	isValidFastshiftRoute: (pathname: string) => boolean;
	isValidFastshiftPartnerRoute: (pathname: string) => boolean;
};

// @ts-ignorek
export const useRoutes: UseRoutes = () => {
	const { pathname } = useLocation();
	const { locale, domainId } = useParams<{
		locale: string;
		domainId: string;
	}>();

	const injectParams = useCallback(
		(path: string, targetDomainId?: string) => {
			return `${locale ? `/${locale}/` : '/'}${path.replace(
				':domainId',
				targetDomainId ?? domainId ?? '',
			)}`;
		},
		[locale, domainId],
	);

	const paths = {
		[PATHS_KEYS.HOME]: injectParams(FASTSHIFT_PATHS[FASTSHIFT_PATH_KEYS.FASTSHIFT]),
		[PATHS_KEYS.SELECTION]: injectParams(FASTSHIFT_PATHS[FASTSHIFT_PATH_KEYS.SELECTION]),
		[PATHS_KEYS.PARTNER_RESOURCES]: injectParams(
			FASTSHIFT_PATHS[FASTSHIFT_PATH_KEYS.RESOURCES_PARTNER_SCOPED],
		),
		[PATHS_KEYS.DOMAIN_SCOPED_OVERVIEW]: domainId
			? injectParams(FASTSHIFT_PATHS[FASTSHIFT_PATH_KEYS.OVERVIEW])
			: null,
		[PATHS_KEYS.DOMAIN_SCOPED_PLANNING]: domainId
			? injectParams(FASTSHIFT_PATHS[FASTSHIFT_PATH_KEYS.PLANNING])
			: null,
		[PATHS_KEYS.DOMAIN_SCOPED_TICKETS]: domainId
			? injectParams(FASTSHIFT_PATHS[FASTSHIFT_PATH_KEYS.TICKETS])
			: null,
		[PATHS_KEYS.DOMAIN_SCOPED_RESOURCES]: domainId
			? injectParams(FASTSHIFT_PATHS[FASTSHIFT_PATH_KEYS.RESOURCES_DOMAIN_SCOPED])
			: null,
		[PATHS_KEYS.EAP_HOME]: injectParams(FASTSHIFT_EAP_HOME_PATH),
	};

	const getPathByDomainId = (path: string, targetDomainId: string) => {
		return injectParams(path, targetDomainId);
	};

	const isValidFastshiftRoute = (pathname: string) => {
		const { eapHome: _, ...rest } = paths;

		return Object.values(rest).includes(pathname);
	};

	const isValidFastshiftPartnerRoute = (pathname: string) => {
		return [paths.selection, paths.partnerResources].includes(pathname);
	};

	return {
		domainId,
		fullPathname: pathname,
		paths,
		getPathByDomainId,
		isValidFastshiftRoute,
		isValidFastshiftPartnerRoute,
	};
};
